// weeklyProgress.js - Modal content for Weekly Production Progress KPI
// Place at: src/pages/components/modalContent/weeklyProgress.js
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useWeeklyProgress } from "../../../hooks/useWeeklyProgress";
import "../styles/weeklyProgress.css";

// Note: card sizing is controlled entirely by weeklyProgress.css.
// Height media queries (≤750px, ≤660px) compress cards so all 4
// fit inside a modal-large at 634 px viewport height.

// ─── Metric Weights ───────────────────────────────────────────────────────────
// Count metrics (units, sqft) weighted 2x higher than maintenance percentages
const METRIC_WEIGHTS = {
  units: 2.0, // Weekly units completed - continuous growth metric
  sqft: 2.0, // SQFT completed - continuous growth metric
  ontime: 1.0, // On-time completion % - maintenance metric
  quality: 1.0, // First pass rate % - maintenance metric
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtValue = (v, unit) => {
  if (unit === "sqft") {
    // Always show sqft in thousands with 1 decimal place and 'k' suffix
    return `${v.toFixed(1)}k`;
  }
  if (unit === "%") return `${v}%`;
  return String(v);
};

// Remove the cap at 100% - return actual percentage rounded to nearest whole number
const pctOf = (actual, goal) =>
  goal === 0 ? 0 : Math.round((actual / goal) * 100);

// ─── Daily Pace Calculation ───────────────────────────────────────────────────
/**
 * Calculate what day of the business week we're in (1-5 for Mon-Fri).
 * Uses the weekStart date from the API response to determine offset.
 *
 * @param {string|null} weekStart - "YYYY-MM-DD" of Monday from API
 * @returns {number} - 1-5 representing Mon-Fri, or 5 if calculation fails
 */
const getCurrentWeekday = (weekStart) => {
  if (!weekStart) return 5; // Default to Friday (end of week) if unknown

  try {
    const monday = new Date(weekStart + "T00:00:00Z");
    const now = new Date();
    const diffMs = now - monday;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Clamp to 1-5 (Mon-Fri). Weekend or future dates default to Friday.
    return Math.max(1, Math.min(5, diffDays + 1));
  } catch {
    return 5;
  }
};

/**
 * Calculate expected cumulative progress by end of current weekday.
 *
 * For a 5-day week with goal G:
 * - Base daily target = Math.floor(G / 5)
 * - Remainder days that need +1 = G % 5
 *
 * Example: G=12 → base=2, remainder=2
 * - Mon: 3, Tue: 3, Wed: 2, Thu: 2, Fri: 2
 * - By end of Wed: should have 3+3+2 = 8
 *
 * @param {number} goal - Weekly goal
 * @param {number} weekday - 1-5 for Mon-Fri
 * @returns {number} - Expected cumulative total by end of this day
 */
const getExpectedByDay = (goal, weekday) => {
  const baseDailyTarget = Math.floor(goal / 5);
  const remainderDays = goal % 5;

  // First N days get base+1, remaining days get base
  let expected = 0;
  for (let day = 1; day <= weekday; day++) {
    expected += baseDailyTarget + (day <= remainderDays ? 1 : 0);
  }

  return expected;
};

/**
 * Determine status for count metrics based on daily pace.
 *
 * @param {number} actual - Current cumulative count
 * @param {number} goal - Weekly goal
 * @param {number} weekday - 1-5 for Mon-Fri
 * @returns {string} - "exceeded" | "ok" | "amber" | "alert"
 */
const statusOfCount = (actual, goal, weekday) => {
  const expected = getExpectedByDay(goal, weekday);
  const pct = goal === 0 ? 0 : (actual / expected) * 100;

  if (actual > expected) return "exceeded"; // Ahead of pace
  if (pct >= 100) return "ok"; // Exactly on pace
  if (pct >= 85) return "amber"; // Within 15% of pace (near target)
  return "alert"; // More than 15% behind pace
};

/**
 * Determine status for percentage metrics (maintenance goals).
 * Uses traditional threshold logic against the weekly goal.
 */
const statusOfPercent = (pct) => {
  if (pct > 100) return "exceeded";
  if (pct >= 100) return "ok";
  if (pct >= 85) return "amber";
  return "alert";
};

/**
 * Unified status function that routes to appropriate logic based on metric type.
 *
 * @param {object} metric - Metric object with id, actual, goal, unit
 * @param {number} weekday - Current weekday (1-5)
 * @returns {string} - Status classification
 */
const statusOf = (metric, weekday) => {
  const isCountMetric = metric.unit === "units" || metric.unit === "sqft";

  if (isCountMetric) {
    return statusOfCount(metric.actual, metric.goal, weekday);
  } else {
    // Percentage metrics use traditional pct-of-goal logic
    const pct = pctOf(metric.actual, metric.goal);
    return statusOfPercent(pct);
  }
};

/**
 * Generate human-readable status label based on metric type and status.
 *
 * @param {string} status - "exceeded" | "ok" | "amber" | "alert"
 * @param {boolean} isCountMetric - true for units/sqft, false for percentages
 * @returns {string} - Display label
 */
const statusLabel = (status, isCountMetric) => {
  if (!isCountMetric) {
    // Percentage metrics keep traditional labels
    return status === "exceeded"
      ? "Exceeded Goal"
      : status === "ok"
        ? "On Target"
        : status === "amber"
          ? "Near Target"
          : "Off Target";
  }

  // Count metrics use daily pace labels
  return status === "exceeded"
    ? "Ahead of Pace"
    : status === "ok"
      ? "On Pace"
      : status === "amber"
        ? "Near Pace"
        : "Behind Pace";
};

const STATUS_COLORS = {
  exceeded: {
    main: "#38a169", // Green for exceeded
    tint: "rgba(56,161,105,0.06)",
    border: "rgba(56,161,105,0.18)",
  },
  ok: {
    main: "#38a169",
    tint: "rgba(56,161,105,0.06)",
    border: "rgba(56,161,105,0.18)",
  },
  amber: {
    main: "#d69e2e",
    tint: "rgba(214,158,46,0.06)",
    border: "rgba(214,158,46,0.18)",
  },
  alert: {
    main: "#cc0000",
    tint: "rgba(204,0,0,0.05)",
    border: "rgba(204,0,0,0.15)",
  },
};

// ─── Segmented progress track ─────────────────────────────────────────────────
const SEGMENTS = 20;

const SegmentedBar = ({ pct, color }) => {
  // Allow filling beyond 20 segments when pct > 100
  const filled = Math.round((Math.min(pct, 150) / 100) * SEGMENTS);
  return (
    <div className="wpm-segment-bar">
      {Array.from({ length: SEGMENTS }, (_, i) => {
        const isFilled = i < filled;
        const isEdge = isFilled && i === filled - 1;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 2,
              background: isFilled ? color : "#e8edf3",
              opacity: isFilled ? 0.45 + (i / Math.max(filled, 1)) * 0.55 : 1,
              boxShadow: isEdge ? `0 0 7px ${color}99` : "none",
              transition: "background 0.3s",
            }}
          />
        );
      })}
    </div>
  );
};

// ─── Animated percentage counter ──────────────────────────────────────────────
const AnimatedPct = ({ value, color }) => {
  const elRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    let start = null;
    const duration = 650;
    const tick = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      if (elRef.current)
        elRef.current.textContent = `${Math.round(eased * value)}%`;
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  return (
    <span ref={elRef} className="wpm-animated-pct" style={{ color }}>
      0%
    </span>
  );
};

// ─── Single metric card ───────────────────────────────────────────────────────
const MetricCard = ({ metric, onNavigate, index, weekday }) => {
  const pct = pctOf(metric.actual, metric.goal);
  const status = statusOf(metric, weekday);
  const colors = STATUS_COLORS[status];
  const cardRef = useRef(null);
  const isCountMetric = metric.unit === "units" || metric.unit === "sqft";

  const handleMouseEnter = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = "translateY(-2px)";
    cardRef.current.style.boxShadow = `0 8px 28px rgba(0,0,0,0.10), 0 0 0 1px ${colors.main}55`;
    cardRef.current.style.borderColor = colors.main;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = "translateY(0)";
    cardRef.current.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)";
    cardRef.current.style.borderColor = colors.border;
  };

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={() => onNavigate(metric.modalTarget)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onNavigate(metric.modalTarget);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="wpm-card"
      style={{
        border: `1px solid ${colors.border}`,
        background: `linear-gradient(135deg, ${colors.tint} 0%, #fff 60%)`,
        animationDelay: `${index * 75}ms`,
      }}
    >
      {/* Left accent bar */}
      <div
        className="wpm-card-accent"
        style={{
          background: `linear-gradient(180deg, ${colors.main} 0%, ${colors.main}77 100%)`,
        }}
      />
      {/* Top row: icon + label + animated pct */}
      <div className="wpm-card-top-row">
        {/* Left: icon badge + text */}
        <div className="wpm-card-left">
          <div
            className="wpm-icon-badge"
            style={{
              background: `${colors.main}14`,
              border: `1px solid ${colors.main}28`,
            }}
          >
            {metric.icon(colors.main)}
          </div>
          <div>
            <div className="wpm-metric-label">{metric.label}</div>
            <div className="wpm-metric-sublabel">{metric.sublabel}</div>
          </div>
        </div>
        {/* Right: percentage + chevron */}
        <div className="wpm-pct-row">
          <AnimatedPct value={pct} color={colors.main} />
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="#c8d3de"
            strokeWidth="2"
            className="wpm-chevron"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </div>
      {/* Segmented progress bar */}
      <SegmentedBar pct={pct} color={colors.main} />
      {/* Bottom row: actual / goal counts + status chip */}
      <div className="wpm-bottom-row">
        <div className="wpm-actual-goal">
          <span className="wpm-actual-value">
            {fmtValue(metric.actual, metric.unit)}
          </span>
          <span className="wpm-slash">/</span>
          <span className="wpm-goal-value">
            {fmtValue(metric.goal, metric.unit)} goal
          </span>
        </div>
        <div
          className="wpm-status-chip"
          style={{
            background: `${colors.main}14`,
            border: `1px solid ${colors.main}30`,
          }}
        >
          <div className="wpm-status-dot" style={{ background: colors.main }} />
          <span className="wpm-status-label" style={{ color: colors.main }}>
            {statusLabel(status, isCountMetric)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────
// Mirrors the card count and approximate height so the modal doesn't jump
// when data arrives. Uses the same .wpm-card class so height media-queries
// apply identically to the real cards.
const SkeletonCard = ({ index }) => (
  <div
    className="wpm-card"
    style={{
      border: "1px solid #e2e8f0",
      background: "#f7fafc",
      animationDelay: `${index * 75}ms`,
      pointerEvents: "none",
    }}
  >
    <div className="wpm-card-accent" style={{ background: "#e2e8f0" }} />
    <div className="wpm-card-top-row">
      <div className="wpm-card-left">
        <div
          className="wpm-icon-badge"
          style={{ background: "#e2e8f0", border: "none" }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div
            style={{
              width: 140,
              height: 12,
              borderRadius: 4,
              background: "#e2e8f0",
            }}
          />
          <div
            style={{
              width: 90,
              height: 9,
              borderRadius: 4,
              background: "#edf2f7",
            }}
          />
        </div>
      </div>
      <div
        style={{
          width: 60,
          height: 32,
          borderRadius: 6,
          background: "#e2e8f0",
        }}
      />
    </div>
    <div
      style={{ height: 6, borderRadius: 4, background: "#e2e8f0", margin: 0 }}
    />
    <div className="wpm-bottom-row">
      <div
        style={{
          width: 80,
          height: 11,
          borderRadius: 4,
          background: "#edf2f7",
        }}
      />
      <div
        style={{
          width: 72,
          height: 20,
          borderRadius: 20,
          background: "#edf2f7",
        }}
      />
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const WeeklyProgress = () => {
  const navigate = useNavigate();
  const { metrics, loading, error, weekStart, weekEnd, refetch } =
    useWeeklyProgress();

  const handleRowClick = (modalTarget) => {
    navigate("/production-milestones", { state: { openModal: modalTarget } });
  };

  // Determine current weekday for daily pace calculations
  const currentWeekday = getCurrentWeekday(weekStart);

  // ─── Weighted average calculation ───────────────────────────────────────────
  // Count metrics (units, sqft) weighted 2x higher than maintenance % (ontime, quality)
  const calculateWeightedAverage = () => {
    let weightedSum = 0;
    let totalWeight = 0;

    metrics.forEach((m) => {
      const pct = pctOf(m.actual, m.goal);
      const weight = METRIC_WEIGHTS[m.id] || 1.0;

      weightedSum += pct * weight;
      totalWeight += weight;
    });

    return totalWeight === 0 ? 0 : Math.round(weightedSum / totalWeight);
  };

  // Count metrics on target using new daily-pace logic for count metrics
  const onTarget = metrics.filter((m) => {
    const status = statusOf(m, currentWeekday);
    return status === "ok" || status === "exceeded";
  }).length;

  const overallPct = calculateWeightedAverage();

  // Overall status color based on weighted average
  const overallStatus =
    overallPct > 100
      ? "exceeded"
      : overallPct >= 100
        ? "ok"
        : overallPct >= 85
          ? "amber"
          : "alert";
  const overallColors = STATUS_COLORS[overallStatus];

  // Week-range label shown in summary header when meta is available
  const weekLabel = weekStart && weekEnd ? `${weekStart} – ${weekEnd}` : null;

  return (
    <div className="wpm-container">
      {/* Summary header */}
      <div className="wpm-summary">
        <div>
          <div className="wpm-summary-label">Weekly Goal Progress</div>
          <div className="wpm-summary-subtext">
            {loading
              ? "Loading metrics…"
              : error
                ? "Could not load data"
                : weekLabel
                  ? `Week of ${weekLabel}`
                  : `${onTarget} of ${metrics.length} metrics on target this week`}
          </div>
        </div>
        <div className="wpm-summary-right">
          {!loading && !error && (
            <>
              <div
                className="wpm-overall-pct"
                style={{ color: overallColors.main }}
              >
                {overallPct}%
              </div>
              <div className="wpm-overall-avg-label">weighted avg</div>
            </>
          )}
        </div>
      </div>

      {/* ── Error state ────────────────────────────────────────────────────── */}
      {error && (
        <div
          style={{
            padding: "18px 20px",
            borderRadius: 10,
            background: "rgba(204,0,0,0.04)",
            border: "1px solid rgba(204,0,0,0.18)",
            color: "#cc0000",
            fontSize: 13,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>{error}</span>
          <button
            onClick={refetch}
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#cc0000",
              background: "rgba(204,0,0,0.08)",
              border: "1px solid rgba(204,0,0,0.25)",
              borderRadius: 6,
              padding: "4px 12px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Loading skeletons ───────────────────────────────────────────────── */}
      {loading &&
        Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} index={i} />)}

      {/* ── Live metric cards ───────────────────────────────────────────────── */}
      {!loading &&
        !error &&
        metrics.map((metric, index) => (
          <MetricCard
            key={metric.id}
            metric={metric}
            onNavigate={handleRowClick}
            index={index}
            weekday={currentWeekday}
          />
        ))}

      <div className="wpm-footer-note">
        Click any card to open detailed analytics on the Production Milestones
        page
      </div>
    </div>
  );
};

export default WeeklyProgress;
